import React, { useState, useEffect } from 'react';
import { Wallet, Coins, ShoppingBag, Send, History, ArrowRightLeft, Lock, ShoppingCart, Sparkles, Info, X, Download, Inbox, CheckCircle2, QrCode, Copy, ExternalLink } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import { toast } from 'sonner';
import TSBScanModal from '../../components/TSBScanModal';

const TABS = [
  { id: 'overview', label: 'Overview', icon: Wallet },
  { id: 'deposit', label: 'Deposit', icon: Inbox },
  { id: 'convert', label: 'Convert', icon: ArrowRightLeft },
  { id: 'p2p', label: 'P2P', icon: Send },
  { id: 'marketplace', label: 'Marketplace', icon: ShoppingBag },
  { id: 'orders', label: 'My Orders', icon: ShoppingCart },
  { id: 'history', label: 'Ledger', icon: History },
];

const TSB = () => {
  const [tab, setTab] = useState('overview');
  const [wallet, setWallet] = useState(null);
  const [settings, setSettings] = useState(null);
  const [showQR, setShowQR] = useState(false);

  useEffect(() => { load(); }, []);
  const load = async () => {
    try {
      const [w, s] = await Promise.all([api.get('/tsb/wallet/me'), api.get('/tsb/settings')]);
      setWallet(w.data.wallet);
      setSettings(s.data);
    } catch (e) {
      const st = e?.response?.status;
      if (st === 401 || st === 403) toast.error('Please sign in again to view your TSB wallet.');
      else console.error('TSB wallet load failed', e);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white pt-28 md:pt-32 pb-16 px-4 md:px-6" data-testid="tsb-page">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-4 mb-6">
          {settings?.coin_logo_url && (
            <img src={settings.coin_logo_url} alt="TSB Coin" className="w-16 h-16 md:w-20 md:h-20 rounded-2xl object-cover shadow-lg shadow-amber-500/30" data-testid="tsb-logo" />
          )}
          <div>
            <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-amber-300 via-yellow-300 to-orange-300 bg-clip-text text-transparent">
              TSB Coin Reward System
            </h1>
            <p className="text-xs text-slate-400">{settings?.coin_tagline || 'The Shopping Bitcoin'} • Closed-Loop Loyalty • 50/50 Cash+TSB Checkout</p>
          </div>
        </div>

        {/* Wallet Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <WalletCard
            label="Cash Balance"
            value={`₹${(wallet?.cash_balance || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`}
            icon={<Wallet className="w-5 h-5" />}
            tone="emerald"
            testid="tsb-cash-card"
          />
          <div className="relative">
            <WalletCard
              label="TSB Points"
              value={`${(wallet?.tsb_balance || 0).toLocaleString(undefined, { maximumFractionDigits: 4 })}`}
              icon={<Coins className="w-5 h-5" />}
              tone="amber"
              testid="tsb-points-card"
            />
            <button
              onClick={() => setShowQR(true)}
              data-testid="tsb-qr-button"
              className="absolute top-3 right-3 p-2 bg-slate-900/80 hover:bg-amber-500 hover:text-black rounded-lg transition-colors"
              title="Show TSB wallet QR code"
            >
              <QrCode className="w-4 h-4" />
            </button>
          </div>
          <div className="bg-slate-800/40 border border-slate-700 rounded-xl p-4" data-testid="tsb-rate-card">
            <div className="text-xs text-slate-400">Current Rate</div>
            <div className="text-2xl font-bold text-purple-300 mt-1">1 TSB = ₹{settings?.current_tsb_rate || 1}</div>
            <div className="text-[10px] text-slate-500 mt-2 flex items-center gap-1">
              <Lock className="w-3 h-3" /> One-way: Cash → TSB only (no reverse)
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="sticky top-24 md:top-28 z-30 -mx-4 md:-mx-6 px-4 md:px-6 bg-slate-950/95 backdrop-blur flex gap-1 overflow-x-auto border-b border-slate-800 mb-6">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`tsb-tab-${t.id}`}
                className={`px-4 py-3 flex items-center gap-2 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                  tab === t.id ? 'border-amber-400 text-amber-300' : 'border-transparent text-slate-400 hover:text-white'
                }`}
              >
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === 'overview' && <OverviewTab wallet={wallet} settings={settings} onReload={load} />}
        {tab === 'deposit' && <DepositTab settings={settings} />}
        {tab === 'convert' && <ConvertTab settings={settings} onDone={load} />}
        {tab === 'p2p' && <P2PTab settings={settings} onDone={load} />}
        {tab === 'marketplace' && <MarketplaceTab settings={settings} onCheckout={load} />}
        {tab === 'orders' && <OrdersTab />}
        {tab === 'history' && <HistoryTab />}
      </div>

      {/* TSB Wallet QR Modal */}
      {showQR && wallet?.address && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setShowQR(false)} data-testid="tsb-qr-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-slate-900 border border-amber-500/40 rounded-2xl p-6 max-w-sm w-full relative">
            <button onClick={() => setShowQR(false)} className="absolute top-3 right-3 text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            <div className="flex items-center gap-2 mb-3">
              {settings?.coin_logo_url && <img src={settings.coin_logo_url} alt="TSB" className="w-10 h-10 rounded-lg" />}
              <div>
                <h3 className="text-lg font-bold">Your TSB Wallet</h3>
                <p className="text-[10px] text-slate-400">Scan to receive TSB / view history</p>
              </div>
            </div>
            <div className="bg-white p-4 rounded-xl flex items-center justify-center mb-4">
              <QRCodeSVG
                value={wallet.address}
                size={220}
                level="M"
                includeMargin={false}
                imageSettings={settings?.coin_logo_url ? {
                  src: settings.coin_logo_url,
                  height: 36,
                  width: 36,
                  excavate: true,
                } : undefined}
              />
            </div>
            <div className="bg-slate-800/60 rounded-lg p-3 mb-3">
              <div className="text-[10px] text-slate-400 uppercase mb-1">Wallet Address</div>
              <div className="font-mono text-xs text-amber-300 break-all" data-testid="tsb-wallet-address">{wallet.address}</div>
              <button
                onClick={() => { navigator.clipboard.writeText(wallet.address); toast.success('Copied!'); }}
                data-testid="tsb-wallet-address-copy"
                className="mt-2 px-3 py-1.5 bg-amber-500 text-black rounded text-xs font-bold flex items-center gap-1"
              >
                <Copy className="w-3 h-3" /> Copy Address
              </button>
            </div>
            <Link
              to={wallet.explorer_url || '#'}
              data-testid="tsb-wallet-explorer-link"
              className="flex items-center justify-center gap-2 w-full py-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-bold"
            >
              <ExternalLink className="w-4 h-4" /> View My History on Explorer
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

const WalletCard = ({ label, value, icon, tone, testid }) => {
  const toneMap = {
    emerald: 'from-emerald-500/20 to-teal-500/10 border-emerald-500/30 text-emerald-300',
    amber:   'from-amber-500/20 to-orange-500/10 border-amber-500/30 text-amber-300',
  };
  return (
    <div className={`bg-gradient-to-br ${toneMap[tone]} border rounded-xl p-4`} data-testid={testid}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-300">{icon}{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  );
};

// ============ Overview ============
const OverviewTab = ({ wallet, settings }) => {
  const levels = settings?.roadmap_levels || [];
  const today = new Date().toISOString().slice(0, 10);
  return (
    <div className="space-y-6">
      <div className="bg-slate-800/40 border border-slate-700 rounded-2xl p-6">
        <h2 className="text-lg font-bold mb-3 flex items-center gap-2"><Info className="w-5 h-5 text-blue-400" /> How TSB Coin Works</h2>
        <ul className="space-y-2 text-sm text-slate-300 list-disc pl-5">
          <li><strong className="text-amber-300">Closed-Loop Loyalty:</strong> TSB ek internal points system hai. Yeh cryptocurrency NAHI hai.</li>
          <li><strong className="text-amber-300">One-Way Conversion:</strong> Cash → TSB hota hai, lekin TSB se Cash withdraw NAHI ho sakta.</li>
          <li><strong className="text-amber-300">50/50 Checkout:</strong> Marketplace par har product 50% Cash + 50% TSB me kharidna hota hai.</li>
          <li><strong className="text-amber-300">P2P Transfer:</strong> Min <strong>{settings?.min_p2p || 0.1} TSB</strong>, decimals tak (precision 7 digits).</li>
          <li><strong className="text-amber-300">Supply Cap:</strong> Total 10 Billion TSB. Govt reserve 30% (2040 tak locked).</li>
        </ul>
        <div className="mt-4 bg-slate-900/60 border border-slate-700 rounded-lg p-3 text-xs">
          <div>Current Rate: <strong className="text-purple-300">1 TSB = ₹{settings?.current_tsb_rate || 1}</strong></div>
          <div>Net Worth: <strong className="text-amber-300">₹{((wallet?.cash_balance || 0) + (wallet?.tsb_balance || 0) * (settings?.current_tsb_rate || 1)).toFixed(2)}</strong></div>
        </div>
      </div>

      {/* 7-Level Pricing Roadmap */}
      <div className="bg-gradient-to-br from-amber-500/10 via-slate-900/60 to-blue-500/10 border border-amber-500/30 rounded-2xl p-6" data-testid="tsb-roadmap">
        <h3 className="text-xl font-bold mb-1 text-amber-300">📈 TSB Coin Roadmap</h3>
        <p className="text-xs text-slate-400 mb-4 italic">The Shopping Bitcoin — Pricing Levels</p>
        <div className="space-y-2">
          {levels.map((lvl) => {
            const active = lvl.start <= today && today <= lvl.end;
            return (
              <div
                key={lvl.level}
                data-testid={`roadmap-level-${lvl.level}`}
                className={`flex items-center gap-3 p-3 rounded-lg border ${active ? 'bg-amber-500/20 border-amber-400 shadow-lg shadow-amber-500/20' : 'bg-slate-900/40 border-slate-700'}`}
              >
                <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold ${active ? 'bg-amber-400 text-black' : 'bg-slate-700 text-slate-300'}`}>
                  L{lvl.level}
                </div>
                <div className="flex-1">
                  <div className="text-xs text-slate-400">
                    {lvl.start} → {lvl.end === '2099-12-31' ? 'onwards' : lvl.end}
                  </div>
                  <div className={`text-base font-bold ${active ? 'text-amber-200' : 'text-white'}`}>
                    1 TSB = {lvl.rate_inr === null ? '???' : `₹${lvl.rate_inr}`} INR
                  </div>
                </div>
                {active && (
                  <span className="text-[10px] font-bold bg-emerald-500 text-black px-2 py-1 rounded-full flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> ACTIVE
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ============ Deposit Tab ============
const DepositTab = ({ settings }) => {
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('upi');
  const [proof, setProof] = useState('');
  const [note, setNote] = useState('');
  const [requests, setRequests] = useState([]);

  useEffect(() => { load(); }, []);
  const load = async () => {
    try { const r = await api.get('/tsb/deposit/requests/me'); setRequests(r.data?.requests || []); }
    catch { /* ignore */ }
  };

  const submit = async () => {
    const a = parseFloat(amount);
    if (!a || a < (settings?.min_deposit || 100)) return toast.error(`Min deposit ₹${settings?.min_deposit || 100}`);
    try {
      await api.post('/tsb/deposit/request', { amount: a, payment_method: method, payment_proof: proof, note });
      toast.success('Deposit request submitted! Wait for admin approval.');
      setAmount(''); setProof(''); setNote('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Request failed');
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="bg-slate-800/40 border border-slate-700 rounded-2xl p-6" data-testid="tsb-deposit-form">
        <h2 className="text-xl font-bold mb-2">Request Deposit</h2>
        <p className="text-xs text-slate-400 mb-4">Min: ₹{settings?.min_deposit || 100} • Manual approval by admin</p>

        {(settings?.deposit_upi_id || settings?.deposit_upi_qr_url || settings?.deposit_bank_account) && (
          <div className="bg-slate-900/60 border border-emerald-700/40 rounded-xl p-4 mb-4">
            <div className="text-xs font-semibold text-emerald-300 mb-3">💳 Payment Details — पहले payment करें, फिर form भरें</div>
            {settings?.deposit_upi_qr_url && (
              <div className="mb-3 flex items-center gap-3">
                <img src={settings.deposit_upi_qr_url} alt="UPI QR" className="w-28 h-28 bg-white rounded-lg p-1" />
                <div className="text-xs">
                  <div className="text-slate-400">Scan to pay via UPI</div>
                  {settings?.deposit_upi_id && (<div className="text-slate-200 font-mono mt-1 break-all">{settings.deposit_upi_id}</div>)}
                </div>
              </div>
            )}
            {!settings?.deposit_upi_qr_url && settings?.deposit_upi_id && (
              <div className="text-xs mb-2"><span className="text-slate-400">UPI ID:</span> <span className="text-slate-200 font-mono">{settings.deposit_upi_id}</span></div>
            )}
            {settings?.deposit_bank_account && (
              <div className="text-xs space-y-0.5 border-t border-slate-700 pt-3 mt-2">
                <div className="text-slate-400 font-semibold mb-1">Bank Transfer:</div>
                {settings?.deposit_bank_holder && <div><span className="text-slate-500">Holder:</span> <span className="text-slate-200">{settings.deposit_bank_holder}</span></div>}
                {settings?.deposit_bank_name && <div><span className="text-slate-500">Bank:</span> <span className="text-slate-200">{settings.deposit_bank_name}</span></div>}
                <div><span className="text-slate-500">A/C:</span> <span className="text-slate-200 font-mono">{settings.deposit_bank_account}</span></div>
                {settings?.deposit_bank_ifsc && <div><span className="text-slate-500">IFSC:</span> <span className="text-slate-200 font-mono">{settings.deposit_bank_ifsc}</span></div>}
              </div>
            )}
            {settings?.deposit_instructions && (
              <div className="text-xs text-amber-300 mt-3 italic whitespace-pre-line">{settings.deposit_instructions}</div>
            )}
            {settings?.deposit_support_whatsapp && (
              <a
                href={`https://wa.me/${String(settings.deposit_support_whatsapp).replace(/\D/g, '')}?text=${encodeURIComponent('नमस्ते, मैंने TSB wallet के लिए deposit किया है। Payment screenshot भेज रहा हूँ।')}`}
                target="_blank" rel="noopener noreferrer"
                data-testid="tsb-deposit-whatsapp-btn"
                className="mt-3 flex items-center justify-center gap-2 w-full py-2.5 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-sm font-semibold"
              >
                💬 WhatsApp पर Screenshot भेजें
              </a>
            )}
          </div>
        )}

        <input type="number" placeholder="Amount (₹)" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="tsb-deposit-amount" className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg mb-3" />
        <select value={method} onChange={(e) => setMethod(e.target.value)} className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg mb-3">
          <option value="upi">UPI</option>
          <option value="bank">Bank Transfer</option>
          <option value="manual">Manual / Other</option>
        </select>
        <input type="text" placeholder="UTR / Transaction ID" value={proof} onChange={(e) => setProof(e.target.value)} className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg mb-3" />
        <textarea placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg mb-3" />
        <button onClick={submit} data-testid="tsb-deposit-submit" className="w-full py-3 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-lg font-bold">Submit Request</button>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-slate-300 mb-2">My Deposits</h3>
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {requests.map((r) => (
            <div key={r.id} className="bg-slate-800/40 border border-slate-700 rounded-lg p-3 text-sm">
              <div className="flex justify-between">
                <span className="font-semibold">₹{r.amount}</span>
                <span className={`text-xs px-2 py-1 rounded ${r.status === 'approved' ? 'bg-emerald-500/20 text-emerald-300' : r.status === 'rejected' ? 'bg-red-500/20 text-red-300' : 'bg-yellow-500/20 text-yellow-300'}`}>{r.status}</span>
              </div>
              <div className="text-xs text-slate-500 mt-1">{new Date(r.created_at).toLocaleString()} • {r.payment_method}</div>
              {r.admin_note && <div className="text-xs text-slate-400 mt-1 italic">&quot;{r.admin_note}&quot;</div>}
            </div>
          ))}
          {requests.length === 0 && <div className="text-slate-500 text-sm">No deposit requests yet</div>}
        </div>
      </div>
    </div>
  );
};

// ============ Convert (Cash → TSB) ============
const ConvertTab = ({ settings, onDone }) => {
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const rate = settings?.current_tsb_rate || 1;
  const tsbOut = amount && rate > 0 ? (parseFloat(amount) / rate).toFixed(7) : 0;

  const doSubmit = async () => {
    const a = parseFloat(amount);
    if (!a || a < 1) return toast.error('Min ₹1');
    setLoading(true);
    try {
      const r = await api.post('/tsb/wallet/convert', { amount_inr: a });
      toast.success(`Converted! ${r.data.tsb_credited} TSB credited.`);
      setAmount('');
      setConfirm(false);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Conversion failed');
    } finally { setLoading(false); }
  };

  return (
    <>
      <div className="bg-slate-800/40 border border-slate-700 rounded-2xl p-6 max-w-md">
        <h2 className="text-xl font-bold mb-2 flex items-center gap-2"><ArrowRightLeft className="w-5 h-5 text-amber-400" /> Convert Cash → TSB</h2>
        <p className="text-xs text-slate-400 mb-4 flex items-center gap-1"><Lock className="w-3 h-3" /> Strictly one-way. TSB cannot be converted back to Cash.</p>
        <label className="text-xs text-slate-400">Cash to convert (₹)</label>
        <input
          type="number" step="0.01" min="1"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="e.g. 1000"
          data-testid="tsb-convert-amount"
          className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg mt-1 mb-3"
        />
        <div className="bg-slate-900/60 border border-amber-500/30 rounded-lg p-3 mb-3 text-sm">
          You will receive: <strong className="text-amber-300 text-lg">{tsbOut} TSB</strong>
          <div className="text-[10px] text-slate-500">Rate: 1 TSB = ₹{rate}</div>
        </div>
        <button
          onClick={() => {
            const a = parseFloat(amount);
            if (!a || a < 1) return toast.error('Min ₹1');
            setConfirm(true);
          }}
          disabled={!amount}
          data-testid="tsb-convert-submit"
          className="w-full py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-black font-bold rounded-lg disabled:opacity-50"
        >
          Review & Convert
        </button>
      </div>

      {/* Confirmation Modal */}
      {confirm && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="tsb-convert-confirm-modal">
          <div className="bg-slate-900 border border-amber-500/40 rounded-2xl p-6 max-w-md w-full relative">
            <button onClick={() => setConfirm(false)} className="absolute top-3 right-3 text-slate-400"><X className="w-5 h-5" /></button>
            <h3 className="text-xl font-bold mb-1">Confirm Conversion</h3>
            <p className="text-xs text-amber-300 italic mb-4">⚠️ Conversion is ONE-WAY. यह वापस Cash में नहीं बदलेगा।</p>
            <div className="bg-slate-800/60 rounded-lg p-4 space-y-2 mb-4">
              <div className="flex justify-between"><span className="text-slate-400">Cash to debit</span><span className="font-bold text-emerald-300">₹{parseFloat(amount).toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">TSB to credit</span><span className="font-bold text-amber-300">{tsbOut} TSB</span></div>
              <div className="flex justify-between text-xs border-t border-slate-700 pt-2"><span className="text-slate-500">Rate</span><span className="text-slate-400">1 TSB = ₹{rate}</span></div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setConfirm(false)} className="flex-1 py-2 bg-slate-700 rounded-lg">Cancel</button>
              <button onClick={doSubmit} disabled={loading} data-testid="tsb-convert-confirm" className="flex-1 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-black font-bold rounded-lg disabled:opacity-50">
                {loading ? 'Converting...' : 'Confirm Convert'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ============ P2P ============
const P2PTab = ({ settings, onDone }) => {
  const [recipient, setRecipient] = useState('');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [recPreview, setRecPreview] = useState(null);
  const [recError, setRecError] = useState('');

  // Whenever recipient changes, auto-lookup if it looks like a UUID/prefix from scan
  useEffect(() => {
    setRecPreview(null);
    setRecError('');
    if (!recipient || recipient.length < 6) return;
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/tsb/wallet/lookup?q=${encodeURIComponent(recipient)}`);
        setRecPreview(r.data);
      } catch (e) {
        if (recipient.length >= 8) setRecError(e.response?.data?.detail || 'Not found');
      }
    }, 400);
    return () => clearTimeout(t);
  }, [recipient]);

  const submit = async () => {
    if (!recipient) return toast.error('Recipient required');
    const a = parseFloat(amount);
    const minP2P = settings?.min_p2p || 0.1;
    if (!a || a < minP2P) return toast.error(`Min ${minP2P} TSB`);
    setLoading(true);
    try {
      const r = await api.post('/tsb/wallet/p2p', { recipient, amount: a, note });
      toast.success(`Sent ${r.data.amount} TSB to ${r.data.recipient.username}`);
      setRecipient(''); setAmount(''); setNote(''); setRecPreview(null);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'P2P failed');
    } finally { setLoading(false); }
  };

  return (
    <>
    <div className="bg-slate-800/40 border border-slate-700 rounded-2xl p-6 max-w-md">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xl font-bold flex items-center gap-2"><Send className="w-5 h-5 text-amber-400" /> P2P TSB Transfer</h2>
        <button
          onClick={() => setScanOpen(true)}
          data-testid="tsb-p2p-scan-btn"
          className="px-3 py-1.5 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg text-sm font-bold flex items-center gap-1 hover:scale-105 transition"
        >
          <QrCode className="w-4 h-4" /> Scan to Pay
        </button>
      </div>
      <p className="text-xs text-slate-400 mb-4">Min: <strong className="text-amber-300">{settings?.min_p2p || 0.1} TSB</strong> • Decimals up to 7 digits</p>
      <label className="text-xs text-slate-400">Recipient (email / username / wallet address)</label>
      <input
        type="text"
        value={recipient}
        onChange={(e) => setRecipient(e.target.value)}
        data-testid="tsb-p2p-recipient"
        placeholder="user@example.com, username, or tsb:<id>"
        className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg mt-1 mb-2"
      />
      {recPreview && (
        <div className="bg-emerald-500/10 border border-emerald-500/40 rounded-lg p-3 mb-3 text-sm" data-testid="tsb-p2p-recipient-preview">
          <div className="text-[10px] text-emerald-300 uppercase">Sending to</div>
          <div className="font-bold text-emerald-200">{recPreview.label}</div>
          <div className="text-[10px] text-slate-500 font-mono">{recPreview.id}</div>
        </div>
      )}
      {recError && !recPreview && (
        <div className="text-xs text-red-300 mb-3">⚠ {recError}</div>
      )}
      <label className="text-xs text-slate-400">Amount (TSB) — supports decimals like 0.1234567</label>
      <input
        type="number" step="0.0000001" min="0.0000001"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        data-testid="tsb-p2p-amount"
        placeholder="0.1234567"
        className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg mt-1 mb-3"
      />
      <label className="text-xs text-slate-400">Note (optional)</label>
      <input
        type="text"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        data-testid="tsb-p2p-note"
        className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg mt-1 mb-3"
      />
      <button
        onClick={submit}
        disabled={loading}
        data-testid="tsb-p2p-submit"
        className="w-full py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold rounded-lg disabled:opacity-50"
      >
        {loading ? 'Sending...' : `Send ${amount || 0} TSB${recPreview ? ' → ' + recPreview.label : ''}`}
      </button>
    </div>

    <TSBScanModal
      open={scanOpen}
      onClose={() => setScanOpen(false)}
      onScan={(value) => {
        setRecipient(value);
        toast.success('QR scanned! Verifying recipient...');
      }}
    />
    </>
  );
};

// ============ Marketplace ============
const MarketplaceTab = ({ settings, onCheckout }) => {
  const [products, setProducts] = useState([]);
  const [confirmProduct, setConfirmProduct] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { load(); }, []);
  const load = async () => {
    try { const r = await api.get('/tsb/products'); setProducts(r.data.products || []); }
    catch { /* ignore */ }
  };

  const buy = async () => {
    if (!confirmProduct) return;
    setLoading(true);
    try {
      const r = await api.post('/tsb/checkout', { product_id: confirmProduct.id, quantity: 1 });
      toast.success(`Order placed! ₹${r.data.payment_breakdown.cash_paid_inr} cash + ${r.data.payment_breakdown.tsb_paid} TSB deducted.`);
      setConfirmProduct(null);
      load();
      onCheckout();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Checkout failed');
    } finally { setLoading(false); }
  };

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {products.map((p) => (
          <div key={p.id} className="bg-slate-800/40 border border-slate-700 rounded-xl p-4 flex flex-col" data-testid={`tsb-product-${p.id}`}>
            <div className="text-xs text-purple-300 font-semibold">{p.vendor_name}</div>
            <div className="text-base font-bold mt-1 mb-2">{p.product_name}</div>
            <div className="text-amber-300 font-bold text-lg">₹{p.price_inr.toLocaleString()}</div>
            <div className="text-[11px] text-slate-400 mt-1">Stock: {p.stock}</div>
            <div className="mt-3 bg-slate-900/60 border border-slate-700 rounded-lg p-2 text-[11px]">
              <div className="text-slate-400 mb-0.5">50/50 Split:</div>
              <div className="text-emerald-300">₹{p.payment_split.cash_inr} cash</div>
              <div className="text-amber-300">+ {p.payment_split.tsb_required} TSB</div>
            </div>
            <button
              onClick={() => setConfirmProduct(p)}
              data-testid={`tsb-buy-${p.id}`}
              className="mt-3 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-black font-bold rounded-lg"
            >
              Buy Now
            </button>
          </div>
        ))}
        {products.length === 0 && (
          <div className="col-span-full text-slate-500 text-sm text-center py-8">No products available yet</div>
        )}
      </div>

      {/* Confirmation Modal */}
      {confirmProduct && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="tsb-checkout-modal">
          <div className="bg-slate-900 border border-amber-500/40 rounded-2xl p-6 max-w-md w-full relative">
            <button onClick={() => setConfirmProduct(null)} className="absolute top-3 right-3 text-slate-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-xl font-bold mb-1">Confirm Purchase</h3>
            <p className="text-xs text-slate-400 mb-4">{confirmProduct.vendor_name}</p>
            <div className="bg-slate-800/60 rounded-lg p-3 mb-4">
              <div className="text-base font-bold">{confirmProduct.product_name}</div>
              <div className="text-2xl font-bold text-amber-300 mt-1">₹{confirmProduct.price_inr.toLocaleString()}</div>
            </div>
            <div className="bg-gradient-to-br from-emerald-500/10 to-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-4">
              <div className="text-xs font-semibold text-amber-300 mb-2">Payment Breakdown</div>
              <div className="flex justify-between items-center py-1 border-b border-slate-700">
                <span className="text-sm text-slate-300">50% Cash</span>
                <span className="font-bold text-emerald-300" data-testid="tsb-confirm-cash">₹{confirmProduct.payment_split.cash_inr}</span>
              </div>
              <div className="flex justify-between items-center pt-1">
                <span className="text-sm text-slate-300">50% TSB</span>
                <span className="font-bold text-amber-300" data-testid="tsb-confirm-tsb">{confirmProduct.payment_split.tsb_required} Points</span>
              </div>
              <div className="text-[10px] text-slate-500 mt-2">Rate: 1 TSB = ₹{settings?.current_tsb_rate || 1}</div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setConfirmProduct(null)} className="flex-1 py-2 bg-slate-700 rounded-lg">Cancel</button>
              <button
                onClick={buy}
                disabled={loading}
                data-testid="tsb-confirm-buy"
                className="flex-1 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-black font-bold rounded-lg disabled:opacity-50"
              >
                {loading ? 'Processing...' : 'Confirm & Pay'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ============ Orders ============
const OrdersTab = () => {
  const [orders, setOrders] = useState([]);
  useEffect(() => {
    api.get('/tsb/orders/me').then(r => setOrders(r.data.orders || []));
  }, []);
  return (
    <div className="space-y-2" data-testid="tsb-orders-list">
      {orders.map((o) => (
        <div key={o.id} className="bg-slate-800/40 border border-slate-700 rounded-lg p-3">
          <div className="flex justify-between flex-wrap gap-2">
            <div>
              <div className="font-bold">{o.product_name}</div>
              <div className="text-xs text-slate-400">{o.vendor_name} • Qty: {o.quantity}</div>
            </div>
            <div className="text-right">
              <div className="font-bold text-amber-300">₹{o.total_inr}</div>
              <div className="text-xs text-slate-400">₹{o.cash_paid} + {o.tsb_paid} TSB</div>
            </div>
          </div>
          <div className="text-[10px] text-slate-500 mt-1">{new Date(o.created_at).toLocaleString()}</div>
        </div>
      ))}
      {orders.length === 0 && <div className="text-slate-500 text-sm text-center py-8">No orders yet</div>}
    </div>
  );
};

// ============ History (Ledger) ============
const HistoryTab = () => {
  const [txs, setTxs] = useState([]);
  useEffect(() => {
    api.get('/tsb/transactions/me').then(r => setTxs(r.data.transactions || []));
  }, []);

  const downloadCSV = async () => {
    try {
      const baseUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      const r = await fetch(`${baseUrl}/api/tsb/ledger/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('Failed');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tsb_ledger_${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Ledger downloaded');
    } catch {
      toast.error('Download failed');
    }
  };

  return (
    <div className="space-y-2" data-testid="tsb-ledger">
      <div className="flex justify-between items-center mb-2">
        <div className="text-sm text-slate-400">Complete Ledger ({txs.length} entries)</div>
        <button onClick={downloadCSV} data-testid="tsb-ledger-download" className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-sm font-bold flex items-center gap-1.5">
          <Download className="w-4 h-4" /> Download CSV (Excel)
        </button>
      </div>
      {txs.map((t) => {
        const isP2P = t.type === 'P2P_TRANSFER';
        const isOut = t.direction === 'OUT';
        const cp = t.counterparty || {};
        return (
          <div key={t.id} className="bg-slate-800/40 border border-slate-700 rounded-lg p-3 text-sm" data-testid={`tsb-tx-${t.id}`}>
            <div className="flex justify-between items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold">{t.type}</span>
                  {t.direction && (
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                      isOut ? 'bg-red-500/20 text-red-300' :
                      t.direction === 'IN' ? 'bg-emerald-500/20 text-emerald-300' :
                      'bg-blue-500/20 text-blue-300'
                    }`}>
                      {t.direction}
                    </span>
                  )}
                </div>
                {isP2P && (
                  <div className="mt-1 text-xs bg-slate-900/60 border border-slate-700 rounded p-2 font-mono">
                    <div className="text-emerald-300">
                      <span className="text-slate-500">FROM:</span> {t.from?.label || '—'}
                      {t.from?.id && <span className="text-slate-600 ml-2">[ID {t.from.id.slice(0, 8)}]</span>}
                    </div>
                    <div className="text-amber-300">
                      <span className="text-slate-500">TO:&nbsp;&nbsp;</span> {t.to?.label || '—'}
                      {t.to?.id && <span className="text-slate-600 ml-2">[ID {t.to.id.slice(0, 8)}]</span>}
                    </div>
                  </div>
                )}
                {!isP2P && t.counterparty?.id && t.direction !== 'SELF' && (
                  <div className="text-xs text-slate-400 mt-0.5">
                    {isOut ? 'To:' : 'From:'} {cp.label}
                  </div>
                )}
                <div className="text-xs text-slate-400 mt-1 break-words">{t.description}</div>
                <div className="text-[10px] text-slate-500">{new Date(t.timestamp).toLocaleString()}</div>
              </div>
              <div className={`text-right font-bold whitespace-nowrap ${t.currency_type === 'TSB' ? 'text-amber-300' : 'text-emerald-300'}`}>
                {isOut ? '-' : '+'}{t.amount} {t.currency_type}
              </div>
            </div>
          </div>
        );
      })}
      {txs.length === 0 && <div className="text-slate-500 text-sm text-center py-8">No transactions</div>}
    </div>
  );
};

export default TSB;
